import React, { FC, useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'
import urlParser from 'js-video-url-parser'

import { HOST_NAME } from '@data/config/url'

import { Element } from '../Element'
import { Link } from '../Link'
import { LinkText, Text } from '../Text'
import { Stack } from '../Stack'
import { Card } from '../Card'

import { Image } from '../Image'
import { ImageKitImage } from '../GraphQLImage'

import { UserDefaultPhoto } from '../User/UserDefaultPhoto'
import { UserItem } from '../User/UserItem'
import { FormattedMarkdown } from '@newComponents'

import {
  PresentationCard_presentation,
  PresentationCard_presentation$key,
} from './__generated__/PresentationCard_presentation.graphql'

const BACKGROUNDS = [
  'https://ik.imagekit.io/guild/social-card-backgrounds/purple-yellow_zbyzdUESq.png',
  'https://ik.imagekit.io/guild/social-card-backgrounds/pink-yellow_UvKUtm5f3.png',
  'https://ik.imagekit.io/guild/social-card-backgrounds/blue-purple_F4x1_B8WX.png',
  'https://ik.imagekit.io/guild/social-card-backgrounds/blue-teal_HwODg9qh1.png',
  'https://ik.imagekit.io/guild/social-card-backgrounds/purple-blue_t3kh8e78Bl.png',
]

interface PresentationCardProps {
  presentation: PresentationCard_presentation$key
  titleNumberOfLines?: number
  numberOfLines?: number
  showCoverPhoto?: boolean
  expand?: boolean
}

const allowedElements = ['a', 'p']

export const PresentationCard: FC<PresentationCardProps> = ({
  presentation,
  titleNumberOfLines,
  numberOfLines = 10,
  showCoverPhoto = true,
  expand = false,
}) => {
  const data = useFragment(
    graphql`
      fragment PresentationCard_presentation on Presentation {
        rowId
        description
        prettyUrl
        title

        videoSourceUrl

        presenterFirstName
        presenterLastName

        presenter {
          ...UserItem_user
        }
      }
    `,
    presentation
  )

  const actorCardTokens = useMemo(() => {
    if (expand) {
      if (!showCoverPhoto) {
        return { flex: 1, paddingTop: 4 }
      }

      return { flex: 1 }
    }

    if (!showCoverPhoto) {
      return { paddingTop: 4 }
    }

    return {}
  }, [expand, showCoverPhoto])

  return (
    <Stack expand={expand}>
      <Element component='ActorCard' tokens={actorCardTokens}>
        <Stack gap={4} expand={expand}>
          {showCoverPhoto && (
            <Link to={`/presentations/${data.prettyUrl}`}>
              <PresentationCoverPhoto presentation={data} />
            </Link>
          )}

          <Stack expand={expand}>
            <Element
              tokens={{
                paddingHorizontal: 4,
                paddingBottom: 0,
                ...(expand ? { flex: 1 } : {}),
              }}
            >
              <Stack gap={4} expand={expand}>
                <Stack gap={4} expand={expand} variant='wrap'>
                  <Link to={`/presentations/${data.prettyUrl}`}>
                    <Text
                      size={6}
                      variant='heading'
                      numberOfLines={titleNumberOfLines}
                    >
                      {data.title}
                    </Text>
                  </Link>

                  {!!data.description && (
                    <Link to={`/presentations/${data.prettyUrl}`}>
                      <FormattedMarkdown
                        allowedElements={allowedElements}
                        formattedVariant='PLAIN_TEXT'
                        numberOfLines={numberOfLines}
                        textSize='$4'
                      >
                        {data.description}
                      </FormattedMarkdown>
                    </Link>
                  )}
                </Stack>

                {data.presenter ? (
                  <UserItem user={data.presenter} />
                ) : (
                  <Stack direction='horizontal' alignItems='center' gap={3}>
                    <UserDefaultPhoto stableId={data.rowId} size={12} />

                    <Text numberOfLines={1}>
                      {data.presenterFirstName} {data.presenterLastName}
                    </Text>
                  </Stack>
                )}
              </Stack>
            </Element>
          </Stack>
        </Stack>
      </Element>
    </Stack>
  )
}

const PresentationCoverPhoto: FC<{
  presentation: PresentationCard_presentation
}> = ({ presentation }) => {
  const videoThumbnailURL = useMemo(() => {
    if (presentation.videoSourceUrl) {
      const videoInfo = urlParser.parse(presentation.videoSourceUrl)

      if (videoInfo?.provider === 'youtube') {
        const photoUrl = urlParser.create({
          format: 'longImage',
          params: { imageQuality: 'maxresdefault' },
          videoInfo,
        })

        if (photoUrl) {
          return photoUrl
        }
      }
    }

    return null
  }, [presentation.videoSourceUrl])

  const socialCardURL = useMemo(() => {
    if (process.env.GUILD_ENV === 'development') {
      const shortRowId = presentation.rowId.substring(0, 8) || '0'

      const backgroundToUse =
        BACKGROUNDS[parseInt(shortRowId, 16) % BACKGROUNDS.length]

      return backgroundToUse
    }

    return `${HOST_NAME}/social/presentation/${presentation.prettyUrl}/card.svg`
  }, [presentation.prettyUrl])

  if (!videoThumbnailURL && process.env.GUILD_ENV === 'development') {
    const shortRowId = presentation.rowId.substring(0, 8) || '0'

    const backgroundToUse =
      BACKGROUNDS[parseInt(shortRowId, 16) % BACKGROUNDS.length]

    return (
      <ImageKitImage
        component='ActorCardBackgroundPhoto'
        imageUrl={backgroundToUse}
        maxWidth='max'
        // maxHeight={30}
        transformations={{
          ar: '2-1',
        }}
      />
    )
  }

  return (
    <Image
      source={{
        uri: videoThumbnailURL || socialCardURL,
      }}
      style={{
        width: '100%',
        maxHeight: 280,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        aspectRatio: '2',
      }}
      // responsive
    />
  )
}
